import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Home, Plus, Trash2, User, Info, ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from "date-fns";
import { sl } from "date-fns/locale";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const CabinsPage = () => {
  const { user, token, hasRole } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [archivedReservations, setArchivedReservations] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [myCabin, setMyCabin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCabin, setSelectedCabin] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    koca: "",
    od_datum: "",
    do_datum: "",
    namen: ""
  });

  // Dinamični seznam koč (namesto trdo kodiranega)
  const [cabins, setCabins] = useState([]);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingCabin, setEditingCabin] = useState(null); // null | {id?, ime, opis}

  const isLeader = ["predsednik", "gospodar", "tajnik", "administrator", "super_admin"].includes(user?.vloga);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [reservationsRes, archivedRes, myCabinRes, cabinsRes] = await Promise.all([
        axios.get(`${API}/cabins/reservations`, { headers }),
        axios.get(`${API}/cabins/reservations/archive`, { headers }),
        axios.get(`${API}/cabins/my-cabin`, { headers }),
        axios.get(`${API}/cabins`, { headers }),
      ]);
      setReservations(
        reservationsRes.data.sort((a, b) => new Date(a.od_datum) - new Date(b.od_datum))
      );
      setArchivedReservations(
        (archivedRes.data || []).sort((a, b) => new Date(b.od_datum) - new Date(a.od_datum))
      );
      setMyCabin(myCabinRes.data);
      const list = Array.isArray(cabinsRes.data) ? cabinsRes.data : [];
      // Normaliziraj: `id` ostane UUID (za CRUD), `slug` je ključ za rezervacije,
      // `name` je prikazna oblika 'Lovska koča "ime"'.
      const normalized = list.map(c => ({ ...c, name: `Lovska koča "${c.ime}"` }));
      setCabins(normalized);

      // Izberi privzeto: upravljana koča, sicer prva iz seznama
      const defaultId =
        myCabinRes.data?.upravljana_koca ||
        list[0]?.slug ||
        "";
      if (defaultId) {
        setSelectedCabin(defaultId);
        setFormData(prev => ({ ...prev, koca: defaultId }));
      }
    } catch (error) {
      toast.error("Napaka pri nalaganju rezervacij");
    } finally {
      setLoading(false);
    }
  };

  // === CRUD za koče (vodstvo) ===
  const openAddCabin = () => {
    setEditingCabin({ ime: "", opis: "" });
    setManageDialogOpen(true);
  };
  const openEditCabin = (c) => {
    setEditingCabin({ id: c.id, ime: c.ime, opis: c.opis || "" });
    setManageDialogOpen(true);
  };
  const saveCabin = async () => {
    if (!editingCabin?.ime?.trim()) {
      toast.error("Vpiši ime koče");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCabin.id) {
        await axios.put(`${API}/cabins/${editingCabin.id}`, { ime: editingCabin.ime, opis: editingCabin.opis }, { headers });
        toast.success("Koča posodobljena");
      } else {
        await axios.post(`${API}/cabins`, { ime: editingCabin.ime, opis: editingCabin.opis }, { headers });
        toast.success("Koča dodana");
      }
      setManageDialogOpen(false);
      setEditingCabin(null);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };
  const deleteCabin = async (c) => {
    if (!window.confirm(`Ali res želiš izbrisati Lovsko kočo "${c.ime}"? Rezervacije bodo ostale.`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/cabins/${c.id}`, { headers });
      toast.success("Koča izbrisana");
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/cabins/reservations`, formData, { headers });
      toast.success("Rezervacija dodana");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Ali ste prepričani, da želite izbrisati rezervacijo?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/cabins/reservations/${id}`, { headers });
      toast.success("Rezervacija izbrisana");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const resetForm = () => {
    setFormData({
      koca: myCabin?.upravljana_koca || selectedCabin,
      od_datum: "",
      do_datum: "",
      namen: ""
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "d. MMM yyyy", { locale: sl });
  };

  const getCabinReservations = (cabinId) => {
    // List shows only upcoming/current reservations (past ones automatically disappear from list,
    // but remain visible in the calendar above).
    const todayStr = new Date().toISOString().split("T")[0];
    return reservations.filter(r => r.koca === cabinId && r.do_datum >= todayStr);
  };

  const getArchivedCabinReservations = (cabinId) => {
    return archivedReservations.filter(r => r.koca === cabinId);
  };

  // Export reservations (Excel or PDF)
  const exportReservations = async (format, leto) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ format });
      if (leto) params.append("leto", leto);
      const res = await axios.get(`${API}/cabins/reservations/export?${params}`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rezervacije-koc-${leto || "vsa-leta"}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Izvoz uspešen (${format.toUpperCase()})`);
    } catch (error) {
      toast.error("Napaka pri izvozu rezervacij");
    }
  };

  // Generate year options (current year + 4 past + 1 future)
  const currentYear = new Date().getFullYear();
  const exportYears = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

  const canManageCabin = (cabinId) => {
    if (user?.vloga === "predsednik") return true;
    if (user?.vloga === "gospodar") return true;
    if (user?.vloga === "upravnik_lovske_koce") {
      if (myCabin?.can_manage_all) return true;
      return myCabin?.upravljana_koca === cabinId;
    }
    return false;
  };

  const canManageAny = hasRole(["predsednik", "gospodar", "upravnik_lovske_koce"]);
  
  // Can see reservation details (who reserved, etc.)
  const canSeeReservationDetails = hasRole(["administrator", "predsednik", "gospodar", "upravnik_lovske_koce"]);

  // Check if a specific day is occupied for a cabin (includes archived past reservations)
  const isDayOccupied = (cabinId, date) => {
    const checkDate = new Date(date);
    checkDate.setHours(12, 0, 0, 0);

    const all = [...reservations, ...archivedReservations];
    return all.some(r => {
      if (r.koca !== cabinId) return false;
      const from = new Date(r.od_datum);
      const to = new Date(r.do_datum);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return checkDate >= from && checkDate <= to;
    });
  };

  // Generate calendar days for current month
  const getCalendarDays = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOffset = () => {
    const firstDay = startOfMonth(currentMonth).getDay();
    // Convert to Monday-first (0 = Monday)
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const getCabinName = (cabinId) => {
    return cabins.find(c => c.slug === cabinId)?.name || cabinId;
  };

  const weekDays = ["Pon", "Tor", "Sre", "Čet", "Pet", "Sob", "Ned"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cabins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Home className="h-8 w-8" />
            Koče
          </h1>
          <p className="text-muted-foreground mt-1">
            Zasedenost lovskih koč
          </p>
        </div>
        
        {canManageAny && (
          <div className="flex flex-wrap items-center gap-2">
            {isLeader && (
              <Button
                variant="outline"
                onClick={openAddCabin}
                data-testid="add-cabin-btn"
                title="Dodaj lovsko kočo"
              >
                <Home className="h-4 w-4 mr-2" />
                Dodaj kočo
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-reservations-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Izvozi
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Excel (.xlsx)</DropdownMenuLabel>
                {exportYears.map((y) => (
                  <DropdownMenuItem
                    key={`xlsx-${y}`}
                    onClick={() => exportReservations("xlsx", y)}
                    data-testid={`export-xlsx-${y}`}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    Leto {y}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => exportReservations("xlsx", null)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                  Vsa leta
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>PDF</DropdownMenuLabel>
                {exportYears.map((y) => (
                  <DropdownMenuItem
                    key={`pdf-${y}`}
                    onClick={() => exportReservations("pdf", y)}
                    data-testid={`export-pdf-${y}`}
                  >
                    <FileText className="h-4 w-4 mr-2 text-red-600" />
                    Leto {y}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => exportReservations("pdf", null)}>
                  <FileText className="h-4 w-4 mr-2 text-red-600" />
                  Vsa leta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-reservation-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova rezervacija
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova rezervacija</DialogTitle>
                <DialogDescription>
                  Rezervirajte kočo za določen termin
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Koča</Label>
                  {myCabin?.upravljana_koca && !myCabin?.can_manage_all ? (
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        <span className="font-medium">{getCabinName(myCabin.upravljana_koca)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Lahko upravljate samo to kočo
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {cabins.map((cabin) => (
                        <Button
                          key={cabin.slug}
                          type="button"
                          variant={formData.koca === cabin.slug ? "default" : "outline"}
                          className="justify-start"
                          onClick={() => setFormData({ ...formData, koca: cabin.slug })}
                          data-testid={`cabin-select-${cabin.slug}`}
                        >
                          <Home className="h-4 w-4 mr-2" />
                          {cabin.name}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="od_datum">Od datuma</Label>
                    <Input
                      id="od_datum"
                      type="date"
                      value={formData.od_datum}
                      onChange={(e) => setFormData({ ...formData, od_datum: e.target.value })}
                      required
                      data-testid="reservation-from-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="do_datum">Do datuma</Label>
                    <Input
                      id="do_datum"
                      type="date"
                      value={formData.do_datum}
                      onChange={(e) => setFormData({ ...formData, do_datum: e.target.value })}
                      required
                      data-testid="reservation-to-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="namen">Namen (opcijsko)</Label>
                  <Textarea
                    id="namen"
                    value={formData.namen}
                    onChange={(e) => setFormData({ ...formData, namen: e.target.value })}
                    placeholder="Namen rezervacije"
                    rows={2}
                    data-testid="reservation-purpose-input"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Prekliči
                  </Button>
                  <Button type="submit" data-testid="save-reservation-btn">
                    Rezerviraj
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Calendar view for each cabin */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Koledar zasedenosti</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-medium min-w-[140px] text-center">
                {format(currentMonth, "LLLL yyyy", { locale: sl })}
              </span>
              <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm mt-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span>Prosto</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span>Zasedeno</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {cabins.map((cabin) => (
              <div key={cabin.slug}>
                <h3 className="font-semibold mb-3">{cabin.name}</h3>
                <div className="border rounded-lg p-2">
                  {/* Week day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {weekDays.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for offset */}
                    {Array.from({ length: getFirstDayOffset() }).map((_, idx) => (
                      <div key={`empty-${idx}`} className="h-8"></div>
                    ))}
                    {/* Actual days */}
                    {getCalendarDays().map((day) => {
                      const occupied = isDayOccupied(cabin.slug, day);
                      const isToday = isSameDay(day, new Date());
                      return (
                        <div
                          key={day.toISOString()}
                          className={`h-8 rounded flex items-center justify-center text-xs font-medium ${
                            occupied 
                              ? "bg-red-500 text-white" 
                              : "bg-green-500 text-white"
                          } ${isToday ? "ring-2 ring-primary ring-offset-1" : ""}`}
                          title={occupied ? "Zasedeno" : "Prosto"}
                        >
                          {format(day, "d")}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for reservations */}
      {cabins.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center">
            <Home className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">V vaši LD še ni nobene lovske koče.</p>
            {isLeader && (
              <Button onClick={openAddCabin} data-testid="empty-add-cabin-btn">
                <Plus className="h-4 w-4 mr-2" /> Dodaj prvo kočo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
      <Tabs value={selectedCabin} onValueChange={setSelectedCabin}>
        <TabsList className="grid w-full max-w-2xl" style={{ gridTemplateColumns: `repeat(${Math.max(1, cabins.length)}, minmax(0, 1fr))` }}>
          {cabins.map((cabin) => (
            <TabsTrigger key={cabin.slug} value={cabin.slug} data-testid={`tab-${cabin.slug}`}>
              {cabin.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {isLeader && cabins.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const c = cabins.find(x => x.slug === selectedCabin);
                if (c) openEditCabin(c);
              }}
              data-testid="rename-cabin-btn"
              title="Preimenuj kočo"
            >
              Preimenuj
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                const c = cabins.find(x => x.slug === selectedCabin);
                if (c) deleteCabin(c);
              }}
              data-testid="delete-cabin-btn"
              title="Izbriši kočo"
            >
              Izbriši
            </Button>
          </div>
        )}

        {cabins.map((cabin) => (
          <TabsContent key={cabin.slug} value={cabin.slug} className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      Rezervacije - {cabin.name}
                    </CardTitle>
                    <CardDescription>
                      {getCabinReservations(cabin.slug).length} prihajajočih rezervacij (pretekle so vidne v koledarju zgoraj)
                    </CardDescription>
                  </div>
                  {getArchivedCabinReservations(cabin.slug).length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowArchive(v => !v)}
                      data-testid="toggle-archive-btn"
                    >
                      {showArchive ? "Skrij arhiv" : `Prikaži arhiv (${getArchivedCabinReservations(cabin.slug).length})`}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {getCabinReservations(cabin.slug).length === 0 ? (
                  <div className="text-center py-12">
                    <Home className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">Ni prihajajočih rezervacij</p>
                    {canManageCabin(cabin.slug) && (
                      <Button className="mt-4" onClick={() => { setFormData({ ...formData, koca: cabin.slug }); setDialogOpen(true); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Dodaj rezervacijo
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getCabinReservations(cabin.slug).map((res) => (
                      <div 
                        key={res.id} 
                        className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                        data-testid={`reservation-${res.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-base font-semibold">
                                {formatDate(res.od_datum)} - {formatDate(res.do_datum)}
                              </Badge>
                            </div>
                            {canSeeReservationDetails && res.namen && (
                              <p className="text-muted-foreground mt-2">{res.namen}</p>
                            )}
                            {canSeeReservationDetails ? (
                              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                                <User className="h-4 w-4" />
                                Rezerviral: {res.rezerviral_ime}
                              </p>
                            ) : (
                              <p className="text-sm text-red-500 mt-2">Zasedeno</p>
                            )}
                          </div>
                          {canManageCabin(cabin.slug) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(res.id)}
                              data-testid={`delete-reservation-${res.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showArchive && getArchivedCabinReservations(cabin.slug).length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-muted-foreground mb-3 text-sm uppercase tracking-wide">
                      Arhiv ({getArchivedCabinReservations(cabin.slug).length})
                    </h4>
                    <div className="space-y-3">
                      {getArchivedCabinReservations(cabin.slug).map((res) => (
                        <div
                          key={res.id}
                          className="p-4 rounded-lg border bg-muted/30 opacity-90"
                          data-testid={`archived-reservation-${res.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="secondary" className="text-base font-semibold">
                                  {formatDate(res.od_datum)} - {formatDate(res.do_datum)}
                                </Badge>
                                <Badge variant="outline" className="text-xs">Arhivirano</Badge>
                              </div>
                              {canSeeReservationDetails && res.namen && (
                                <p className="text-muted-foreground mt-2">{res.namen}</p>
                              )}
                              {canSeeReservationDetails && res.rezerviral_ime && (
                                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                                  <User className="h-4 w-4" />
                                  Rezerviral: {res.rezerviral_ime}
                                </p>
                              )}
                            </div>
                            {canManageCabin(cabin.slug) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDelete(res.id)}
                                data-testid={`delete-archived-reservation-${res.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
      )}

      {/* Add/Edit Cabin Dialog (leadership only) */}
      <Dialog open={manageDialogOpen} onOpenChange={(o) => { setManageDialogOpen(o); if (!o) setEditingCabin(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCabin?.id ? "Preimenuj kočo" : "Dodaj lovsko kočo"}</DialogTitle>
            <DialogDescription>
              Ime bo prikazano v obliki <em>Lovska koča &quot;{editingCabin?.ime || "ime"}&quot;</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ime *</Label>
              <Input
                value={editingCabin?.ime || ""}
                onChange={(e) => setEditingCabin({ ...editingCabin, ime: e.target.value })}
                placeholder="npr. Krvavica"
                data-testid="cabin-form-name"
                autoFocus
              />
            </div>
            <div>
              <Label>Opis (neobvezno)</Label>
              <Textarea
                rows={2}
                value={editingCabin?.opis || ""}
                onChange={(e) => setEditingCabin({ ...editingCabin, opis: e.target.value })}
                placeholder="Kratek opis lokacije, kapacitete …"
                data-testid="cabin-form-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageDialogOpen(false)}>Prekliči</Button>
            <Button onClick={saveCabin} data-testid="cabin-form-save">Shrani</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CabinsPage;
